<script>
  import { CARDS_PER_PAGE_FACE } from '../../lib/logic/config.js';
  import { IMAGE_TIER, getFaceLoadTier } from '../../lib/logic/image-load-order.js';
  import CardGrid from './CardGrid.svelte';

  let {
    paperIndex = 0,
    isMobile = false,
    currentLocation = 0,
    isFlipped = false,
    isUnflippingBackward = false,
    zIndex = 0,
    allCardsData = [],
    currencySymbol = '$',
    onSelect,
    onZoom,
    onTradeQuantity,
    overrideBackIndices = null,
    isVisibleSpread = false,
  } = $props();

  // Calculate card index ranges for front and back faces
  const frontIndices = $derived.by(() => {
    if (isMobile) {
      const start = paperIndex * CARDS_PER_PAGE_FACE;
      return { start, end: start + CARDS_PER_PAGE_FACE };
    }
    // Desktop
    if (paperIndex === 0) {
      return { start: -1, end: -1 }; // Blank cover
    }
    const start = CARDS_PER_PAGE_FACE + (paperIndex - 1) * CARDS_PER_PAGE_FACE * 2;
    return { start, end: start + CARDS_PER_PAGE_FACE };
  });

  // Image fetch order by flip distance (IMAGE_LOAD_ORDER.md). A pinned
  // tab-jump page shows its overridden back face while it flips, so that face
  // is on screen no matter how far its paper is from the new location.
  const frontLoadTier = $derived(
    getFaceLoadTier({ paperIndex, face: 'front', currentLocation, isMobile })
  );
  const backLoadTier = $derived(
    overrideBackIndices
      ? IMAGE_TIER.VISIBLE
      : getFaceLoadTier({ paperIndex, face: 'back', currentLocation, isMobile })
  );

  const backIndices = $derived.by(() => {
    if (overrideBackIndices) return overrideBackIndices;
    if (isMobile) {
      return { start: -1, end: -1 }; // No back on mobile
    }
    // Desktop
    if (paperIndex === 0) {
      return { start: 0, end: CARDS_PER_PAGE_FACE }; // First 9 cards on cover back
    }
    const frontEnd =
      CARDS_PER_PAGE_FACE + (paperIndex - 1) * CARDS_PER_PAGE_FACE * 2 + CARDS_PER_PAGE_FACE;
    return { start: frontEnd, end: frontEnd + CARDS_PER_PAGE_FACE };
  });
</script>

<div
  class="paper"
  class:flipped={isFlipped}
  class:is-unflipping-backward={isUnflippingBackward}
  class:is-visible-spread={isVisibleSpread}
  data-index={paperIndex}
  style:z-index={zIndex}
>
  <div class="front">
    <div class="page-content">
      <CardGrid
        startIndex={frontIndices.start}
        endIndex={frontIndices.end}
        loadTier={frontLoadTier}
        {allCardsData}
        {currencySymbol}
        {onSelect}
        {onZoom}
        {onTradeQuantity}
      />
    </div>
  </div>
  <div class="back">
    <div class="page-content">
      <CardGrid
        startIndex={backIndices.start}
        endIndex={backIndices.end}
        loadTier={backLoadTier}
        {allCardsData}
        {currencySymbol}
        {onSelect}
        {onZoom}
        {onTradeQuantity}
      />
    </div>
  </div>
</div>

<style>
  .paper {
    width: 100%;
    transform-origin: left center;
    transition: transform var(--dur-page-flip) var(--ease-page-flip);
    will-change: auto;
    border-radius: 0 var(--page-corner-radius, 0.3rem) var(--page-corner-radius, 0.3rem) 0;
    background-color: transparent;
    box-shadow:
      0 2px 5px rgba(0, 0, 0, 0.1),
      1px 0 0 rgba(0, 0, 0, 0.05),
      2px 0 0 rgba(0, 0, 0, 0.03),
      inset 3px 0 8px -2px rgba(0, 0, 0, 0.1);
    transform-style: preserve-3d;
    display: grid;
    grid-area: 1 / 1 / -1 / -1;
  }

  /* Temporarily raise z-index for page animating backward (un-flipping).
     2501 to sit above z-boosted pinned pages (2500) during tab jumps. */
  .paper.is-unflipping-backward {
    z-index: 2501 !important;
  }

  /* Only the visible spread animates its foil. Pausing is pure CSS — no
     per-frame JS and no IntersectionObserver — and it takes the sheen from up
     to 144 slots down to the ~18 the user can actually see. */
  .paper:not(.is-visible-spread) :global(.foil-sheen) {
    animation-play-state: paused;
  }

  .paper.flipped {
    transform: rotateY(-180deg);
    box-shadow:
      0 2px 5px rgba(0, 0, 0, 0.1),
      -1px 0 0 rgba(0, 0, 0, 0.05),
      -2px 0 0 rgba(0, 0, 0, 0.03),
      inset -3px 0 8px -2px rgba(0, 0, 0, 0.1);
    border-radius: var(--page-corner-radius) 0 0 var(--page-corner-radius);
  }

  /* Front and Back Faces */
  .front,
  .back {
    width: 100%;
    overflow: hidden;
    backface-visibility: hidden;
    background-color: var(--page-bg-color, #272b30);
    background-image: radial-gradient(rgba(255, 255, 255, 0.07) 1.5px, transparent 1.5px);
    background-size: 4.5px 4.5px;
    display: block;
    grid-area: 1 / 1 / -1 / -1;
  }

  .front {
    z-index: 1;
    border-radius: 0 var(--page-corner-radius, 0.3rem) var(--page-corner-radius, 0.3rem) 0;
    transform: translateZ(0px);
  }

  .back {
    transform: rotateY(180deg);
    z-index: 0;
    border-radius: var(--page-corner-radius, 0.3rem) 0 0 var(--page-corner-radius);
    box-shadow: inset 5px 0px 15px -5px rgba(0, 0, 0, 0.3);
  }

  /* Content Container */
  .page-content {
    padding: var(--page-outer-padding, 0.6rem);
    box-sizing: border-box;
    position: relative;
    display: block;
    background-color: transparent;
  }

  /* Spine shadow - front (right side, spine on left) */
  .front .page-content::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: var(--inner-page-padding, 1.6rem);
    background: linear-gradient(
      to right,
      rgba(0, 0, 0, 0.7) 0%,
      rgba(0, 0, 0, 0.4) 60%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 1;
    transition: width 0.1s;
  }

  /* Spine shadow - back (left side, spine on right) */
  .back .page-content::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    width: var(--inner-page-padding, 1.6rem);
    background: linear-gradient(
      to left,
      rgba(0, 0, 0, 0.7) 0%,
      rgba(0, 0, 0, 0.4) 60%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 1;
    transition: width 0.1s;
  }

  /* Mobile touch optimizations. Flip duration is NOT set here — it comes
     from the --dur-page-flip token (tokens.css), shared with divider tabs
     and the JS unflip timeout. tokens.css carries its own touch-device
     override (hover: none, pointer: coarse) of --dur-page-flip to 450ms,
     vs 600ms on desktop; that's the source of the mobile-vs-desktop
     difference, not anything in this file. */
  @media (hover: none) and (pointer: coarse) {
    .paper {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    }

    .paper.flipped {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    }

    .back {
      box-shadow: none;
    }
  }

  /* Desktop: adjust page content padding for spine */
  @media (min-width: 768px) {
    .front .page-content {
      padding-left: calc(var(--page-outer-padding, 0.6rem) + var(--inner-page-padding, 1.6rem));
      padding-right: var(--page-outer-padding, 0.6rem);
    }

    .back .page-content {
      padding-right: calc(var(--page-outer-padding, 0.6rem) + var(--inner-page-padding, 1.6rem));
      padding-left: var(--page-outer-padding, 0.6rem);
    }
  }
</style>
